import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { EDITION, ASSETS } from "@/data/content";
import { Reveal } from "@/lib/reveal";

export const Edition = () => {
  const [active, setActive] = useState(0);
  const group = EDITION.groups[active];

  return (
    <section id="edition" data-testid="edition" className="relative bg-cream py-24 md:py-32">
      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-10 lg:grid-cols-12 lg:gap-16">
        {/* Integrated visual */}
        <Reveal className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <div className="relative overflow-hidden rounded-2xl shadow-[0_24px_60px_rgba(42,32,21,0.22)]">
              <img src={ASSETS.piglets} alt="Free-range piglets on a regenerative farm" className="h-[420px] w-full object-cover md:h-[520px]" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-7">
                <span className="font-body text-xs uppercase tracking-[0.3em] text-carbonlight">The 2026 Edition</span>
                <p className="mt-2 font-display text-3xl font-medium text-cream">~200 pages, full colour, 100% compostable.</p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Issue plan */}
        <div className="lg:col-span-7">
          <Reveal>
            <span className="font-body text-xs uppercase tracking-[0.3em] text-ochre">{EDITION.kicker}</span>
            <h2 className="mt-4 font-display text-4xl font-medium text-ink sm:text-5xl">Being written now.</h2>
            <p className="mt-5 max-w-xl font-body text-base leading-relaxed text-ink/70">{EDITION.intro}</p>
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-2 border-b border-clay" data-testid="edition-tabs">
            {EDITION.groups.map((g, i) => (
              <button
                key={g.name}
                data-testid={`edition-tab-${i}`}
                onClick={() => setActive(i)}
                className={`relative px-5 py-3 font-body text-sm font-medium transition-colors ${active === i ? "text-carbon" : "text-ink/50 hover:text-ink"}`}
              >
                {g.name}
                {active === i && <motion.span layoutId="edition-underline" className="absolute inset-x-3 -bottom-px h-0.5 bg-carbon" />}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 grid gap-x-8 gap-y-7"
            >
              {group.items.map((it) => (
                <article key={it.title} className="group flex gap-4 border-t border-clay/70 pt-5">
                  <span className="mt-1 shrink-0 rounded bg-carbon/12 px-2.5 py-1 font-body text-xs font-semibold tracking-wide text-carbondark">{it.pages} pp</span>
                  <div>
                    <h3 className="font-display text-xl font-medium text-forest transition-colors group-hover:text-terracotta">{it.title}</h3>
                    <p className="mt-0.5 font-body text-xs uppercase tracking-wider text-ink/50">{it.by}</p>
                    <p className="mt-1.5 font-body text-sm leading-relaxed text-ink/70">{it.desc}</p>
                  </div>
                </article>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
